/**
 * Tenant Impersonation banner + helpers (iter308g).
 *
 * The Super Admin clicks "View as X" → we store the SA's original
 * token in sessionStorage.original_token, swap the active token to
 * the impersonation JWT, and redirect to /admin. This component is
 * mounted at the root of every page; when an impersonation token is
 * detected in storage, it pins a sticky banner to the top of the
 * viewport with an Exit button.
 *
 * Exit restores the original token and redirects back to
 * /super-admin/tenants. The backend audit-logs both start and end.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, ArrowLeftCircle } from 'lucide-react';

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + '/api';

const ORIGINAL_TOKEN_KEY = 'original_token_pre_impersonation';
const IMP_INFO_KEY = 'impersonation_info';

// Public helper used by SuperAdminTenants to KICK OFF an impersonation.
export async function startImpersonation(tenantId) {
  const original = localStorage.getItem('token');
  if (!original) {
    throw new Error('No active session');
  }
  const { data } = await axios.post(
    `${API}/super-admin/impersonate/${tenantId}`,
    {},
    { headers: { Authorization: `Bearer ${original}` } }
  );
  // Stash the SA's token so we can restore it later.
  sessionStorage.setItem(ORIGINAL_TOKEN_KEY, original);
  sessionStorage.setItem(
    IMP_INFO_KEY,
    JSON.stringify({
      tenant_id: data.tenant_id,
      tenant_name: data.tenant_name,
      tenant_slug: data.tenant_slug,
      started_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + data.expires_in_seconds * 1000).toISOString(),
    })
  );
  // Swap active token & redirect into the tenant admin UI.
  localStorage.setItem('token', data.impersonation_token);
  // Also bust any cached branding so the new tenant's brand identity
  // loads when we hit /admin.
  sessionStorage.removeItem('tenant-branding-v1');
  window.location.assign('/admin');
}

export async function endImpersonation() {
  const original = sessionStorage.getItem(ORIGINAL_TOKEN_KEY);
  // Fire-and-forget audit log entry — we still proceed even if it fails.
  try {
    await axios.post(
      `${API}/super-admin/impersonate/end`,
      {},
      { headers: { Authorization: `Bearer ${original}` } }
    );
  } catch (_e) {
    // intentional: never block the exit on an audit-log failure
  }
  if (original) {
    localStorage.setItem('token', original);
  }
  sessionStorage.removeItem(ORIGINAL_TOKEN_KEY);
  sessionStorage.removeItem(IMP_INFO_KEY);
  sessionStorage.removeItem('tenant-branding-v1');
  window.location.assign('/super-admin/tenants');
}

function _readImpInfo() {
  try {
    const raw = sessionStorage.getItem(IMP_INFO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export default function ImpersonationBanner() {
  const [info, setInfo] = useState(_readImpInfo);

  useEffect(() => {
    // Re-read on route changes / storage changes (e.g., other tabs).
    const onStorage = () => setInfo(_readImpInfo());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!info) return null;

  const expiresAt = info.expires_at ? new Date(info.expires_at) : null;
  const minutesLeft = expiresAt
    ? Math.max(0, Math.round((expiresAt - Date.now()) / 60000))
    : null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-amber-950 shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        <div className="flex-1 leading-tight">
          <span className="font-semibold">Impersonating:</span>{' '}
          <span className="font-mono">{info.tenant_name}</span>{' '}
          <span className="text-amber-800 hidden sm:inline">
            (read-only · {minutesLeft != null ? `${minutesLeft}m left` : ''})
          </span>
        </div>
        <button
          onClick={endImpersonation}
          data-testid="impersonation-exit-btn"
          className="inline-flex items-center gap-1 bg-amber-950 text-amber-50 hover:bg-amber-900 rounded px-3 py-1 text-xs font-medium"
        >
          <ArrowLeftCircle className="w-3.5 h-3.5" />
          Exit · back to Super Admin
        </button>
      </div>
    </div>
  );
}
